import { HttpClient, HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { ChatIdentityService } from './chat-identity.service';
import { HelperContextService } from '../features/helper/helper-context.service';
import { environment } from '../../environments/environment';

/** Every outcome the widget acts on. 'feature_disabled' is kept apart from 'error'
 *  deliberately: one hides the widget silently while chat ships dark, the other is a
 *  fault worth showing. */
export type ChatApiStatus =
  'ok' | 'validation' | 'forbidden' | 'not_found' | 'conflict' | 'feature_disabled' | 'error';

export interface ChatApiOutcome<T> {
  status: ChatApiStatus;
  value: T | null;
  /** The API's own wording. Refusals are shown verbatim, never paraphrased. */
  message: string | null;
}

/** Mirrors ChatDutyDto. There is no reason field: the API stores the break reason but
 *  never returns it, so nothing here can render one. */
export interface ChatDuty {
  onDutyCount: number;
  meOnBreak: boolean;
  breakUntil: string | null;
}

/** Mirrors ChatConversationDto, the body of a successful POST /conversations. */
export interface ChatStarted {
  id: string;
  status: string;
  afterHours: boolean;
  createdAt: string;
}

/**
 * Every /api/v2/chat call the survey app makes, and nothing else. No state, no signals,
 * no Supabase. It never throws: a failure is a status, because the caller is usually a
 * click handler on a tablet with two bars of signal.
 *
 * Paths follow ChatEndpoints.cs — break and off-duty sit directly under /api/v2/chat,
 * not under /duty.
 */
@Injectable({ providedIn: 'root' })
export class ChatApiService {
  private readonly http = inject(HttpClient);
  private readonly identity = inject(ChatIdentityService);
  private readonly helper = inject(HelperContextService);

  private readonly base = `${environment.chatApiUrl}/api/v2/chat`;

  duty(): Promise<ChatApiOutcome<ChatDuty>> {
    return this.wrap(this.http.get<ChatDuty>(`${this.base}/duty`, this.opts()));
  }

  heartbeat(): Promise<ChatApiOutcome<null>> {
    return this.post(`/duty/heartbeat`, {});
  }

  startBreak(minutes: number, reason: string | null): Promise<ChatApiOutcome<null>> {
    return this.post(`/break`, { minutes, reason: trimmedOrNull(reason) });
  }

  /** DELETE /break, its own route. A zero-minute POST would be refused as a 400 by
   *  StartChatBreakCommandHandler, so "I'm back" would silently never work. */
  endBreak(): Promise<ChatApiOutcome<null>> {
    return this.wrap(this.http.delete<null>(`${this.base}/break`, this.opts()));
  }

  /**
   * Best effort, on pagehide. sendBeacon cannot set headers, so both credentials travel
   * in the body — never the query string, because URLs land in access logs, proxy logs
   * and browser history and a bearer token in any of those is a leaked credential.
   * /offduty, not /duty/offduty: it is the one route outside the X-API-Key middleware.
   */
  offDutyBeacon(): void {
    const accessToken = this.identity.token();
    if (accessToken === null) return;
    if (typeof navigator.sendBeacon !== 'function') return;

    navigator.sendBeacon(
      `${this.base}/offduty`,
      new Blob([JSON.stringify({ apiKey: environment.chatApiKey, accessToken })],
        { type: 'application/json' }),
    );
  }

  /**
   * targetUserId null is the pool: the conversation is queued and whoever is free
   * claims it. A named target is claimed for that person as the row is created, so
   * it never reaches anybody else's queue — asking one admin a question used to ring
   * every CSR's bubble with "New chat waiting".
   */
  start(firstMessage: string, targetUserId: string | null = null):
    Promise<ChatApiOutcome<ChatStarted>> {
    const body = firstMessage.trim();
    if (body.length === 0) {
      return Promise.resolve({ status: 'validation', value: null, message: null });
    }

    // originApp is fixed here rather than passed in. A survey caller claiming "portal"
    // would put a staff-to-staff chat behind the business-hours gate.
    //
    // helperId is read here for the same reason. Field helpers share one login, so without
    // the name this person picked on the way in, every helper reached customer service as
    // the shared account and a CSR could not tell two chats apart. The API only trusts the
    // id — it looks the name up itself, from rows that have no login of their own.
    return this.post(`/conversations`, {
      subject: null,
      body,
      originApp: 'survey',
      targetUserId,
      helperId: this.helper.helper()?.id ?? null,
    });
  }

  invite(conversationId: string, userId: string): Promise<ChatApiOutcome<null>> {
    return this.post(`/conversations/${conversationId}/participants`, { userId });
  }

  /** 'resolved' is the staff entry in ChatCloseReasons; the handler refuses free text. */
  close(conversationId: string): Promise<ChatApiOutcome<null>> {
    return this.post(`/conversations/${conversationId}/close`, { reason: 'resolved' });
  }

  /** Staff only, and only until the end of the same business day. Returns it to active. */
  reopen(conversationId: string): Promise<ChatApiOutcome<null>> {
    return this.post(`/conversations/${conversationId}/reopen`, {});
  }

  private post<T>(path: string, body: unknown): Promise<ChatApiOutcome<T>> {
    return this.wrap(this.http.post<T>(`${this.base}${path}`, body, this.opts()));
  }

  private opts(): { headers: HttpHeaders } {
    let headers = new HttpHeaders({ 'X-API-Key': environment.chatApiKey });

    const accessToken = this.identity.token();
    if (accessToken !== null) {
      headers = headers.set('Authorization', `Bearer ${accessToken}`);
    }

    return { headers };
  }

  private async wrap<T>(call: Observable<T>): Promise<ChatApiOutcome<T>> {
    try {
      return { status: 'ok', value: await firstValueFrom(call), message: null };
    } catch (error) {
      return { status: statusFor(error), value: null, message: messageFor(error) };
    }
  }
}

function trimmedOrNull(value: string | null): string | null {
  const trimmed = value?.trim() ?? '';
  return trimmed === '' ? null : trimmed;
}

function statusFor(error: unknown): ChatApiStatus {
  if (!(error instanceof HttpErrorResponse)) return 'error';

  switch (error.status) {
    case 400: return 'validation';
    case 403: return 'forbidden';
    case 404: return 'not_found';
    case 409: return 'conflict';
    // The toggle being off. The widget hides itself; it never shows an error.
    case 503: return 'feature_disabled';
    default: return 'error';
  }
}

function messageFor(error: unknown): string | null {
  if (!(error instanceof HttpErrorResponse)) return null;

  // The rate limiter answers 429 with no body this shape, so without a line of its
  // own it fell through to whatever the caller says when it has nothing, and a
  // refusal that means "the API stopped answering" read as "that did not work".
  // The bucket is per API key, so it is every browser's traffic together, not this
  // person's — on a tablet with two bars, saying so is the difference between
  // trying again and giving up.
  if (error.status === 429) return 'chat is busy right now — try that again in a moment';

  const body = error.error as { error?: string } | null;
  return typeof body?.error === 'string' ? body.error : null;
}
